import * as PIXI from 'pixi.js';
import { soundManager } from './SoundManager';

export enum GemType { RED = 0, BLUE = 1, GREEN = 2, YELLOW = 3, PURPLE = 4, CYAN = 5, ORANGE = 6 }
export enum SpecialType { NONE = 'none', BOMB = 'bomb', ROCKET = 'rocket', COLOR_BOMB = 'color_bomb' }
export interface Position { x: number; y: number }
export interface GoalConfig { type: 'score' | 'destroy' | 'collect' | 'drop'; targetType?: string; targetCount: number; }
export interface GameConfig {
  width: number; height: number; gemTypes: number; moveLimit: number;
  goals?: GoalConfig[]; obstacles?: Array<{ type: 'ice' | 'chain' | 'crates'; positions: Position[]; }>;
}

interface Gem { type: GemType; special: SpecialType; level: number; sprite: PIXI.Container | null; gridX: number; gridY: number; }

const GEM_COLORS: Record<GemType, number[]> = {
  [GemType.RED]:    [0xff6b81, 0xff4757, 0xc0392b],
  [GemType.BLUE]:   [0x7c83ff, 0x3742fa, 0x2c3ed0],
  [GemType.GREEN]:  [0x7bed9f, 0x2ed573, 0x27ae60],
  [GemType.YELLOW]: [0xffd93d, 0xffa502, 0xe67e22],
  [GemType.PURPLE]: [0xc4a0ff, 0x8854d0, 0x6c3db8],
  [GemType.CYAN]:   [0x48dbfb, 0x00d4ff, 0x00a8cc],
  [GemType.ORANGE]: [0xff9ff3, 0xff6348, 0xe55039],
};
const GEM_GLOW: Record<GemType, number> = {
  [GemType.RED]: 0xff6b81, [GemType.BLUE]: 0x5352ed, [GemType.GREEN]: 0x7bed9f,
  [GemType.YELLOW]: 0xffbe76, [GemType.PURPLE]: 0xa29bfe, [GemType.CYAN]: 0x48dbfb, [GemType.ORANGE]: 0xff9ff3,
};
const SP_GLOW: Record<SpecialType, number> = {
  [SpecialType.NONE]: 0xffffff, [SpecialType.BOMB]: 0xff4444, [SpecialType.ROCKET]: 0x00ffaa, [SpecialType.COLOR_BOMB]: 0xffaa00,
};

// ─── Gem drawing with gradients ─────────────────────────────────
function drawShapePath(g: PIXI.Graphics, type: GemType, r: number) {
  switch (type) {
    case GemType.RED: g.circle(0, 0, r); break;
    case GemType.BLUE: { const h = r * 0.85; g.rect(-h, -h, h * 2, h * 2); break; }
    case GemType.GREEN: g.moveTo(0, -r); g.lineTo(r * 0.866, r * 0.5); g.lineTo(-r * 0.866, r * 0.5); g.closePath(); break;
    case GemType.YELLOW: g.moveTo(0, -r); g.lineTo(r * 0.7, 0); g.lineTo(0, r); g.lineTo(-r * 0.7, 0); g.closePath(); break;
    case GemType.PURPLE: for (let i = 0; i < 5; i++) { const a = (i * 2 * Math.PI) / 5 - Math.PI / 2; i === 0 ? g.moveTo(Math.cos(a) * r, Math.sin(a) * r) : g.lineTo(Math.cos(a) * r, Math.sin(a) * r); } g.closePath(); break;
    case GemType.CYAN: for (let i = 0; i < 6; i++) { const a = (i * Math.PI) / 3; i === 0 ? g.moveTo(Math.cos(a) * r, Math.sin(a) * r) : g.lineTo(Math.cos(a) * r, Math.sin(a) * r); } g.closePath(); break;
    case GemType.ORANGE: { const ir = r * 0.45; for (let i = 0; i < 10; i++) { const a = (i * Math.PI) / 5 - Math.PI / 2; const rad = i % 2 === 0 ? r : ir; i === 0 ? g.moveTo(Math.cos(a) * rad, Math.sin(a) * rad) : g.lineTo(Math.cos(a) * rad, Math.sin(a) * rad); } g.closePath(); } break;
  }
}

function gemGradTexture(cols: number[], size: number): PIXI.Texture {
  const cv = document.createElement('canvas');
  cv.width = size * 2; cv.height = size * 2;
  const ctx = cv.getContext('2d')!;
  const cx = size, cy = size, r = size * 0.85;
  const grad = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.3, r * 0.1, cx, cy, r);
  grad.addColorStop(0, `#${cols[0].toString(16).padStart(6, '0')}`);
  grad.addColorStop(0.6, `#${cols[1].toString(16).padStart(6, '0')}`);
  grad.addColorStop(1, `#${cols[2].toString(16).padStart(6, '0')}`);
  ctx.fillStyle = grad;
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
  return PIXI.Texture.from(cv);
}

function mkSprite(type: GemType, special: SpecialType, level: number, cs: number, obsType?: string, obsHits?: number): PIXI.Container {
  const c = new PIXI.Container();
  const r = cs * 0.34;
  const cols = GEM_COLORS[type];

  if (special === SpecialType.COLOR_BOMB) {
    // Dark sphere with rainbow orbiting dots
    const bg = new PIXI.Graphics(); bg.circle(0, 0, r * 1.15); bg.fill({ color: 0x1a1a2e }); c.addChild(bg);
    const border = new PIXI.Graphics(); border.circle(0, 0, r * 1.15);
    border.stroke({ color: 0xffaa00, width: 2, alpha: 0.8 }); c.addChild(border);
    const dots = new PIXI.Graphics();
    for (let i = 0; i < 8; i++) { const a = (i * Math.PI * 2) / 8; const orbitR = r * 0.6;
      const orbitCols = [0xff4757, 0xffa502, 0x2ed573, 0x00d4ff, 0x3742fa, 0x8854d0, 0xff6348, 0x7bed9f];
      dots.circle(Math.cos(a) * orbitR, Math.sin(a) * orbitR, r * 0.1); dots.fill({ color: orbitCols[i], alpha: 0.9 }); }
    dots.circle(0, 0, r * 0.12); dots.fill({ color: 0xffffff, alpha: 0.8 }); c.addChild(dots);
    // Pulsing outer glow
    const glow = new PIXI.Graphics(); glow.circle(0, 0, r * 1.4);
    glow.fill({ color: 0xffaa00, alpha: 0.15 }); c.addChild(glow);
    c.userData = { glow }; // for ticker animation
  } else {
    const sp = special !== SpecialType.NONE;
    const gemR = r * (sp ? 1.0 : 0.92);

    // Outer glow ring
    const gr = sp ? r * 1.35 : r * 1.2;
    const gc = sp ? SP_GLOW[special] : GEM_GLOW[type];
    const glow = new PIXI.Graphics(); drawShapePath(glow, type, gr);
    glow.fill({ color: gc, alpha: sp ? 0.3 : 0.15 }); c.addChild(glow);

    // Gradient body using sprite
    const texSize = Math.ceil(gemR * 2.5);
    const tex = gemGradTexture(cols, texSize);
    const body = new PIXI.Sprite(tex);
    body.anchor.set(0.5); body.width = gemR * 2; body.height = gemR * 2;
    // Mask to shape
    const mask = new PIXI.Graphics(); drawShapePath(mask, type, gemR); mask.fill({ color: 0xffffff });
    c.addChild(body); c.addChild(mask); body.mask = mask;

    // White highlight (top-left)
    const hl = new PIXI.Graphics();
    hl.ellipse(-gemR * 0.2, -gemR * 0.22, gemR * 0.35, gemR * 0.2);
    hl.fill({ color: 0xffffff, alpha: 0.45 }); c.addChild(hl);

    // Inner shadow edge
    const edge = new PIXI.Graphics(); drawShapePath(edge, type, gemR);
    edge.stroke({ color: 0x000000, width: 1.5, alpha: 0.2 }); c.addChild(edge);

    if (sp) {
      const b = new PIXI.Graphics();
      const s = r * 0.6;
      if (special === SpecialType.BOMB) {
        // Bomb: dark sphere with fuse
        b.circle(0, 0, s * 0.75); b.fill({ color: 0x2c2c2c });
        b.stroke({ color: 0x555555, width: 1.5 });
        // Fuse
        b.moveTo(s * 0.15, -s * 0.6); b.quadraticCurveTo(s * 0.5, -s * 1.2, s * 0.85, -s * 1.0);
        b.stroke({ color: 0x999999, width: 2.5 });
        // Spark
        b.circle(s * 0.85, -s * 1.0, s * 0.22); b.fill({ color: 0xffaa00, alpha: 0.95 });
        b.circle(s * 0.85, -s * 1.0, s * 0.1); b.fill({ color: 0xffffff, alpha: 0.7 });
        if (level >= 2) {
          // Double bomb indicator
          b.circle(-s * 0.6, s * 0.6, s * 0.3); b.fill({ color: 0xff4444, alpha: 0.8 });
        }
      } else if (special === SpecialType.ROCKET) {
        // Rocket: sleek body with fins and nose cone
        b.rect(-s * 0.22, -s * 0.85, s * 0.44, s * 1.35); b.fill({ color: 0xe0e0e0 });
        b.stroke({ color: 0xaaaaaa, width: 1 });
        b.moveTo(-s * 0.22, -s * 0.85); b.lineTo(0, -s * 1.3); b.lineTo(s * 0.22, -s * 0.85); b.closePath();
        b.fill({ color: 0xff3333 });
        // Fins
        b.moveTo(-s * 0.22, s * 0.45); b.lineTo(-s * 0.55, s * 0.85); b.lineTo(-s * 0.22, s * 0.25); b.closePath();
        b.fill({ color: 0x3366ff });
        b.moveTo(s * 0.22, s * 0.45); b.lineTo(s * 0.55, s * 0.85); b.lineTo(s * 0.22, s * 0.25); b.closePath();
        b.fill({ color: 0x3366ff });
        // Window
        b.circle(0, -s * 0.1, s * 0.12); b.fill({ color: 0x88ddff, alpha: 0.8 });
        b.stroke({ color: 0x666666, width: 1 });
      }
      c.addChild(b);
    }
  }

  // Obstacle overlay
  if (obsType && obsHits && obsHits > 0) {
    const ov = new PIXI.Graphics();
    if (obsType === 'ice') {
      ov.roundRect(-r * 1.1, -r * 1.1, r * 2.2, r * 2.2, 6);
      ov.fill({ color: 0xa8d8ea, alpha: 0.35 * (obsHits / 2) });
      ov.stroke({ color: 0xd4f0ff, width: 2, alpha: 0.6 });
      if (obsHits >= 2) { ov.circle(r * 0.35, -r * 0.35, r * 0.12); ov.fill({ color: 0xffffff, alpha: 0.5 }); }
    } else if (obsType === 'chain') {
      for (let i = 0; i < obsHits; i++) { const a = (Math.PI * 2 * i) / obsHits;
        ov.circle(Math.cos(a) * r * 0.7, Math.sin(a) * r * 0.7, r * 0.32);
        ov.fill({ color: 0x777777 }); ov.stroke({ color: 0xaaaaaa, width: 1.5 }); }
    } else if (obsType === 'crates') {
      ov.roundRect(-r * 1.1, -r * 1.1, r * 2.2, r * 2.2, 4);
      ov.fill({ color: 0x8b6914, alpha: 0.55 });
      ov.stroke({ color: 0x654321, width: 2 });
      for (let i = 0; i < obsHits; i++) { ov.circle(-r * 0.4 + i * r * 0.4, r * 0.65, r * 0.1); ov.fill({ color: 0xffaa00, alpha: 0.85 }); }
    }
    c.addChild(ov);
  }
  return c;
}

// ─── Easing ─────────────────────────────────────────────────────
const eOB = (t: number) => 1 + 2.70158 * Math.pow(t - 1, 3) + 1.70158 * Math.pow(t - 1, 2);
const eOBo = (t: number): number => { const n1 = 7.5625, d1 = 2.75; if (t < 1 / d1) return n1 * t * t; if (t < 2 / d1) return n1 * (t -= 1.5 / d1) * t + 0.75; if (t < 2.5 / d1) return n1 * (t -= 2.25 / d1) * t + 0.9375; return n1 * (t -= 2.625 / d1) * t + 0.984375; };
const eOC = (t: number) => 1 - Math.pow(1 - t, 3);
const eIQ = (t: number) => t * t;

function tw(obj: any, props: Record<string, number>, dur: number, ease = eOC): Promise<void> {
  return new Promise(resolve => {
    const s: Record<string, number> = {}; for (const k in props) s[k] = obj[k];
    const t0 = performance.now();
    const tick = () => { const p = Math.min((performance.now() - t0) / dur, 1), e = ease(p); for (const k in props) obj[k] = s[k] + (props[k] - s[k]) * e; p < 1 ? requestAnimationFrame(tick) : resolve(); };
    requestAnimationFrame(tick);
  });
}

// ─── Particle system ────────────────────────────────────────────
class Parts {
  private p: { s: PIXI.Graphics; vx: number; vy: number; life: number; dec: number }[] = [];
  constructor(private par: PIXI.Container) {}
  burst(x: number, y: number, color: number, n = 12, spd = 3, sz = 4) {
    for (let i = 0; i < n; i++) {
      const a = (Math.PI * 2 * i) / n + (Math.random() - 0.5) * 0.5;
      const sp = spd * (0.6 + Math.random() * 0.8);
      const g = new PIXI.Graphics(); g.circle(0, 0, sz * (0.5 + Math.random() * 0.5));
      g.fill({ color, alpha: 1 }); g.x = x; g.y = y; this.par.addChild(g);
      this.p.push({ s: g, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 1.5, life: 1, dec: 1 / (25 + Math.random() * 15) });
    }
  }
  sparkle(x: number, y: number, color: number, n = 8, spd = 2) {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = spd * (0.5 + Math.random());
      const g = new PIXI.Graphics();
      // Star shape
      g.moveTo(0, -3); g.lineTo(1, -1); g.lineTo(3, 0); g.lineTo(1, 1); g.lineTo(0, 3); g.lineTo(-1, 1); g.lineTo(-3, 0); g.lineTo(-1, -1); g.closePath();
      g.fill({ color, alpha: 1 }); g.x = x; g.y = y; this.par.addChild(g);
      this.p.push({ s: g, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 1, life: 1, dec: 1 / (30 + Math.random() * 20) });
    }
  }
  update() {
    for (let i = this.p.length - 1; i >= 0; i--) {
      const q = this.p[i]; q.s.x += q.vx; q.s.y += q.vy; q.vy += 0.07; q.life -= q.dec;
      q.s.alpha = Math.max(0, q.life); q.s.scale.set(q.life);
      if (q.life <= 0) { q.s.destroy(); this.p.splice(i, 1); }
    }
  }
}

// ─── Main game class ────────────────────────────────────────────
export class Match3Board {
  private app: PIXI.Application;
  private cfg: GameConfig;
  private board: (Gem | null)[][] = [];
  private obs: ({ type: string; hits: number } | null)[][] = [];
  private cs = 60;
  private bC: PIXI.Container; private bgL: PIXI.Container; private gL: PIXI.Container; private fxL: PIXI.Container;
  private parts: Parts;
  private dragging: Gem | null = null;
  private dSX = 0; private dSY = 0;
  private busy = false;
  private score = 0;
  private movesLeft: number;
  private goals: { type: string; targetType?: string; targetCount: number; currentCount: number }[] = [];
  private chainQueue: Gem[] = []; // chain reaction queue
  private animTime = 0;

  public onScore?: (s: number) => void;
  public onMoves?: (m: number) => void;
  public onGoals?: (g: any[]) => void;
  public onEnd?: (ok: boolean, sc: number) => void;

  constructor(app: PIXI.Application, cfg: GameConfig) {
    this.app = app; this.cfg = cfg; this.movesLeft = cfg.moveLimit;
    this.bC = new PIXI.Container(); this.app.stage.addChild(this.bC);
    this.bgL = new PIXI.Container(); this.bC.addChild(this.bgL);
    this.gL = new PIXI.Container(); this.bC.addChild(this.gL);
    this.fxL = new PIXI.Container(); this.bC.addChild(this.fxL);
    this.parts = new Parts(this.fxL);
    this.goals = (cfg.goals || []).map(g => ({ ...g, currentCount: 0 }));
    this.dim();
  }

  private dim() {
    const mxW = this.app.screen.width - 8, mxH = this.app.screen.height - 8;
    this.cs = Math.min(Math.floor(mxW / this.cfg.width), Math.floor(mxH / this.cfg.height), 80);
    this.bC.x = (this.app.screen.width - this.cs * this.cfg.width) / 2;
    this.bC.y = (this.app.screen.height - this.cs * this.cfg.height) / 2;
  }
  private gp(gx: number, gy: number) { return { x: gx * this.cs + this.cs / 2, y: gy * this.cs + this.cs / 2 }; }

  async init() {
    this.dim(); this.build(); this.input(); this.drawBg(); this.renderAll();
    this.app.ticker.add(() => { this.parts.update(); this.animTick(); });
  }

  // Animated pulsing effects
  private animTick() {
    this.animTime += 0.03;
    // Pulse special gem glows
    for (let y = 0; y < this.cfg.height; y++) for (let x = 0; x < this.cfg.width; x++) {
      const g = this.board[y]?.[x];
      if (g?.sprite?.userData?.glow) {
        const pulse = 0.12 + Math.sin(this.animTime * 3) * 0.08;
        g.sprite.userData.glow.alpha = pulse;
        g.sprite.userData.glow.scale.set(1 + Math.sin(this.animTime * 2) * 0.05);
      }
    }
  }

  // ─── Build ─────────────────────────────────────────────────────
  private build() {
    const { width: W, height: H } = this.cfg;
    this.board = Array.from({ length: H }, () => Array(W).fill(null));
    this.obs = Array.from({ length: H }, () => Array(W).fill(null));

    for (const obs of (this.cfg.obstacles || [])) {
      for (const p of obs.positions) {
        if (p.x >= 0 && p.x < W && p.y >= 0 && p.y < H) {
          this.obs[p.y][p.x] = {
            type: obs.type,
            hits: obs.type === 'ice' ? 2 : obs.type === 'chain' ? 2 : obs.type === 'crates' ? 3 : 1
          };
        }
      }
    }

    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      this.board[y][x] = this.makeGem(x, y, (Math.floor(Math.random() * this.cfg.gemTypes) as GemType), SpecialType.NONE, 0);
    }

    let m = this.findMatches(), safety = 0;
    while (m.length > 0 && safety < 500) {
      for (const mt of m) for (const p of mt) {
        const old = this.board[p.y][p.x];
        if (old) { if (old.sprite && old.sprite.parent) old.sprite.parent.removeChild(old.sprite); if (old.sprite) old.sprite.destroy(); this.board[p.y][p.x] = null; }
        this.board[p.y][p.x] = this.makeGem(p.x, p.y, (Math.floor(Math.random() * this.cfg.gemTypes) as GemType), SpecialType.NONE, 0);
      }
      m = this.findMatches(); safety++;
    }
  }

  private makeGem(gx: number, gy: number, type: GemType, sp: SpecialType, lvl: number): Gem {
    const o = this.obs[gy]?.[gx];
    const sprite = mkSprite(type, sp, lvl, this.cs, o?.type, o?.hits);
    const p = this.gp(gx, gy); sprite.x = p.x; sprite.y = p.y;
    return { type, special: sp, level: lvl, sprite, gridX: gx, gridY: gy };
  }

  // ─── Rendering ─────────────────────────────────────────────────
  private drawBg() {
    this.bgL.removeChildren();
    const { width: W, height: H } = this.cfg, s = this.cs;
    const bg = new PIXI.Graphics(); bg.roundRect(-8, -8, W * s + 16, H * s + 16, 16);
    bg.fill({ color: 0x0a0a1e }); bg.stroke({ color: 0x2a2a6a, width: 3, alpha: 0.6 }); this.bgL.addChild(bg);
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      const c = new PIXI.Graphics(); c.roundRect(x * s + 2, y * s + 2, s - 4, s - 4, 8);
      c.fill({ color: (x + y) % 2 === 0 ? 0x121230 : 0x0e0e28 }); this.bgL.addChild(c);
    }
  }

  private renderAll() {
    while (this.gL.children.length > 0) {
      const c = this.gL.children[0];
      this.gL.removeChild(c); if (c) c.destroy();
    }
    for (let y = 0; y < this.cfg.height; y++) for (let x = 0; x < this.cfg.width; x++) {
      const g = this.board[y][x];
      if (g) {
        const o = this.obs[y]?.[x];
        g.sprite = mkSprite(g.type, g.special, g.level, this.cs, o?.type, o?.hits);
        const p = this.gp(x, y); g.sprite.x = p.x; g.sprite.y = p.y;
        this.gL.addChild(g.sprite);
      }
    }
  }

  // ─── Input ─────────────────────────────────────────────────────
  private input() {
    this.gL.eventMode = 'static';
    this.gL.on('pointerdown', (e: PIXI.FederatedPointerEvent) => {
      if (this.busy) return;
      const l = this.gL.toLocal(e.global);
      const gx = Math.floor(l.x / this.cs), gy = Math.floor(l.y / this.cs);
      if (gx < 0 || gx >= this.cfg.width || gy < 0 || gy >= this.cfg.height) return;
      const gem = this.board[gy]?.[gx]; if (!gem) return;
      this.dragging = gem; this.dSX = l.x; this.dSY = l.y;
    });
    this.gL.on('pointermove', (e: PIXI.FederatedPointerEvent) => {
      if (!this.dragging || this.busy) return;
      const l = this.gL.toLocal(e.global);
      const dx = l.x - this.dSX, dy = l.y - this.dSY;
      if (Math.abs(dx) > this.cs * 0.3 && Math.abs(dy) > this.cs * 0.3) return;
      if (Math.abs(dx) > this.cs * 0.3 || Math.abs(dy) > this.cs * 0.3) {
        const gem = this.dragging; this.dragging = null;
        let tx = gem.gridX, ty = gem.gridY;
        if (Math.abs(dx) > Math.abs(dy)) tx += dx > 0 ? 1 : -1; else ty += dy > 0 ? 1 : -1;
        this.trySwap(gem, tx, ty);
      }
    });
    const cancel = () => { this.dragging = null; };
    this.gL.on('pointerup', cancel); this.gL.on('pointerupoutside', cancel);
  }

  // ─── Swap ──────────────────────────────────────────────────────
  private async trySwap(gem: Gem, tx: number, ty: number) {
    const { width: W, height: H } = this.cfg;
    if (tx < 0 || tx >= W || ty < 0 || ty >= H) return;
    const other = this.board[ty]?.[tx]; if (!other) return;
    if (!this.adj(gem, other)) return;

    if (gem.special !== SpecialType.NONE || other.special !== SpecialType.NONE) {
      if (this.movesLeft <= 0) return;
      this.busy = true;
      this.movesLeft--;
      this.onMoves?.(this.movesLeft);
      const activator = gem.special !== SpecialType.NONE ? gem : other;
      const target = activator === gem ? other : gem;
      await this.activateSpecialWithChain(activator, target);
      this.busy = false;
      if (this.movesLeft <= 0) { this.renderAll(); this.checkEnd(); }
      return;
    }

    this.busy = true;
    await this.aSwap(gem, other);
    this.gSwap(gem, other);
    const mt = this.findMatches();
    if (mt.length > 0) {
      this.movesLeft--;
      this.onMoves?.(this.movesLeft);
      await this.proc(mt);
    } else {
      this.gSwap(gem, other);
      await this.aSwap(gem, other);
    }
    this.busy = false;
    if (this.movesLeft <= 0) { this.renderAll(); this.checkEnd(); }
  }

  // ─── Match detection ───────────────────────────────────────────
  private findMatches() {
    const ms: { x: number; y: number }[][] = [];
    const { width: W, height: H } = this.cfg;
    for (let y = 0; y < H; y++) for (let x = 0; x < W - 2; x++) {
      const a = this.board[y][x], b = this.board[y][x + 1], c = this.board[y][x + 2];
      if (a && b && c && a.type === b.type && b.type === c.type) {
        const m = [{ x, y }, { x: x + 1, y }, { x: x + 2, y }];
        let n = x + 3; while (n < W && this.board[y][n]?.type === a.type) m.push({ x: n, y }), n++;
        ms.push(m); x = n - 1;
      }
    }
    for (let x = 0; x < W; x++) for (let y = 0; y < H - 2; y++) {
      const a = this.board[y][x], b = this.board[y + 1][x], c = this.board[y + 2][x];
      if (a && b && c && a.type === b.type && b.type === c.type) {
        const m = [{ x, y }, { x, y: y + 1 }, { x, y: y + 2 }];
        let n = y + 3; while (n < H && this.board[n]?.[x]?.type === a.type) m.push({ x, y: n }), n++;
        ms.push(m); y = n - 1;
      }
    }
    return ms;
  }

  private findCross(matches: { x: number; y: number }[][]): Gem | null {
    const cnt = new Map<string, number>();
    for (const m of matches) for (const p of m) { const k = `${p.x},${p.y}`; cnt.set(k, (cnt.get(k) || 0) + 1); }
    for (const [k, c] of cnt) {
      if (c >= 2) { const [gx, gy] = k.split(',').map(Number); return this.board[gy]?.[gx] || null; }
    }
    return null;
  }

  // ─── Chain reaction system ─────────────────────────────────────
  private async activateSpecialWithChain(activator: Gem, target: Gem) {
    // Kill activator and target, then process cascade
    await this.doSpecialEffect(activator, target);
    if (activator.sprite !== null) this.kill(activator);
    if (target.sprite !== null && this.board[target.gridY]?.[target.gridX] === target) this.kill(target);

    // Process chain reactions from any special gems that were in the blast
    await this.processChainQueue();

    // Drop + fill
    await this.drop();
    await this.fill();
    this.renderAll();

    // Check for new matches from cascade
    const mt2 = this.findMatches();
    if (mt2.length > 0) {
      await this.proc(mt2);
    }
  }

  private async processChainQueue() {
    let safety = 0;
    while (this.chainQueue.length > 0 && safety < 100) {
      safety++;
      const gem = this.chainQueue.shift()!;
      // Check gem still exists and hasn't been already killed
      if (!gem.sprite) continue;
      if (this.board[gem.gridY]?.[gem.gridX] !== gem) continue;

      this.kill(gem); // Remove from board, score points

      switch (gem.special) {
        case SpecialType.BOMB: await this.doBombEffect(gem); break;
        case SpecialType.ROCKET: await this.doRocketEffect(gem); break;
        case SpecialType.COLOR_BOMB: {
          // Need a target type — pick the most common adjacent type
          const targetType = this.mostCommonAdjacentType(gem) ?? gem.type;
          await this.doColorBombEffect(gem, targetType);
          break;
        }
      }
    }
  }

  private mostCommonAdjacentType(gem: Gem): GemType | null {
    const counts = new Map<GemType, number>();
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      const nx = gem.gridX + dx, ny = gem.gridY + dy;
      const g = this.board[ny]?.[nx];
      if (g && g.special === SpecialType.NONE) counts.set(g.type, (counts.get(g.type) || 0) + 1);
    }
    let best: GemType | null = null, bestCount = 0;
    for (const [t, c] of counts) if (c > bestCount) { best = t; bestCount = c; }
    return best;
  }

  // ─── Special gem effects (no board modification) ──────────────
  private async doSpecialEffect(activator: Gem, target: Gem) {
    if (activator.special === SpecialType.BOMB) await this.doBombEffect(activator);
    else if (activator.special === SpecialType.ROCKET) await this.doRocketEffect(activator);
    else if (activator.special === SpecialType.COLOR_BOMB) await this.doColorBombEffect(activator, target.type);
  }

  private async doBombEffect(gem: Gem) {
    soundManager.bombExplode();
    const px = this.gp(gem.gridX, gem.gridY);
    await Promise.all([this.flash(0xff4444, 0.4, 200), this.shake(6, 250), this.eRing(px.x, px.y, 0xff4444, this.cs * 3.5, 400)]);
    this.parts.burst(px.x, px.y, 0xff4444, 25, 5, 5);
    this.parts.sparkle(px.x, px.y, 0xffaa00, 12, 4);

    const rad = gem.level >= 2 ? 2 : 1;
    for (let dy = -rad; dy <= rad; dy++) for (let dx = -rad; dx <= rad; dx++) {
      const nx = gem.gridX + dx, ny = gem.gridY + dy;
      if (nx >= 0 && nx < this.cfg.width && ny >= 0 && ny < this.cfg.height) {
        if (this.obs[ny]?.[nx]) this.reduceObs(nx, ny);
        const g = this.board[ny][nx];
        if (g) {
          if (g.special !== SpecialType.NONE && g !== gem) {
            this.chainQueue.push(g); // Chain reaction!
          } else {
            this.score += 10; this.gUpdate(g); this.kill(g);
          }
        }
      }
    }
    this.onScore?.(this.score);
  }

  private async doRocketEffect(gem: Gem) {
    soundManager.rocketLaunch();
    const px = this.gp(gem.gridX, gem.gridY);
    await Promise.all([this.flash(0x00ff88, 0.3, 150), this.shake(3, 150),
      this.sweep(px.x, px.y, true, 0x00ff88, this.cfg.width * this.cs, 350),
      this.sweep(px.x, px.y, false, 0x00ccff, this.cfg.height * this.cs, 350)]);
    this.parts.burst(px.x, px.y, 0x00ff88, 20, 5, 5);

    for (let x = 0; x < this.cfg.width; x++) { if (this.obs[gem.gridY]?.[x]) this.reduceObs(x, gem.gridY); }
    for (let y = 0; y < this.cfg.height; y++) { if (this.obs[y]?.[gem.gridX]) this.reduceObs(gem.gridX, y); }

    for (let x = 0; x < this.cfg.width; x++) {
      const g = this.board[gem.gridY]?.[x];
      if (g && g !== gem) {
        if (g.special !== SpecialType.NONE) this.chainQueue.push(g);
        else { this.score += 10; this.gUpdate(g); this.kill(g); }
      }
    }
    for (let y = 0; y < this.cfg.height; y++) {
      const g = this.board[y]?.[gem.gridX];
      if (g && g !== gem) {
        if (g.special !== SpecialType.NONE) this.chainQueue.push(g);
        else { this.score += 10; this.gUpdate(g); this.kill(g); }
      }
    }
    this.onScore?.(this.score);
  }

  private async doColorBombEffect(gem: Gem, targetType: GemType) {
    soundManager.colorBomb();
    const px = this.gp(gem.gridX, gem.gridY);
    const color = GEM_COLORS[targetType]?.[1] ?? 0xffaa00;
    await Promise.all([this.cWave(px.x, px.y, color, Math.max(this.app.screen.width, this.app.screen.height), 600),
      this.flash(color, 0.3, 250), this.shake(5, 300)]);
    this.parts.burst(px.x, px.y, color, 30, 5, 5);

    for (let y = 0; y < this.cfg.height; y++) for (let x = 0; x < this.cfg.width; x++) {
      const g = this.board[y]?.[x];
      if (g && g.type === targetType) {
        if (g.special !== SpecialType.NONE && g !== gem) this.chainQueue.push(g);
        else { this.score += 10; this.gUpdate(g); this.kill(g); }
      }
    }
    this.onScore?.(this.score);
  }

  // ─── Cascade processing ────────────────────────────────────────
  private async proc(matches: { x: number; y: number }[][]) {
    let current = matches;
    let cascadeSafety = 0;
    while (current.length > 0 && cascadeSafety < 50) {
      cascadeSafety++;
      const crossGem = this.findCross(current);

      for (const match of current) {
        const ml = match.length;
        const specialsInMatch: Gem[] = [];
        for (const p of match) {
          const g = this.board[p.y]?.[p.x];
          if (g && g.special !== SpecialType.NONE) specialsInMatch.push(g);
          if (this.obs[p.y]?.[p.x]) this.reduceObs(p.x, p.y);
        }

        // Determine special gem to create
        let createSp: { x: number; y: number; type: GemType; sp: SpecialType; lvl: number } | null = null;
        if (crossGem) createSp = { x: crossGem.gridX, y: crossGem.gridY, type: crossGem.type, sp: SpecialType.ROCKET, lvl: 1 };
        else if (ml === 4) { const ci = 1; const g = this.board[match[ci].y]?.[match[ci].x]; if (g) createSp = { x: match[ci].x, y: match[ci].y, type: g.type, sp: SpecialType.BOMB, lvl: 1 }; }
        else if (ml >= 5) { const ci = 2; const g = this.board[match[ci].y]?.[match[ci].x]; if (g) createSp = { x: match[ci].x, y: match[ci].y, type: g.type, sp: SpecialType.COLOR_BOMB, lvl: 1 }; }

        const gemsToUpgrade: Gem[] = [];
        const toDestroy = new Set<string>();

        // Always destroy matched gems
        for (const p of match) toDestroy.add(`${p.x},${p.y}`);

        // Activate specials in match + chain reactions
        for (const sp of specialsInMatch) {
          toDestroy.add(`${sp.gridX},${sp.gridY}`);
          if (ml >= 4 && sp.level === 1) gemsToUpgrade.push(sp);

          // Queue chain activation of nearby specials
          const rad = sp.special === SpecialType.BOMB ? (sp.level >= 2 ? 2 : 1) : Math.max(this.cfg.width, this.cfg.height);
          if (sp.special === SpecialType.BOMB) {
            for (let dy = -rad; dy <= rad; dy++) for (let dx = -rad; dx <= rad; dx++) {
              const nx = sp.gridX + dx, ny = sp.gridY + dy;
              if (nx >= 0 && nx < this.cfg.width && ny >= 0 && ny < this.cfg.height) {
                toDestroy.add(`${nx},${ny}`);
                if (this.obs[ny]?.[nx]) this.reduceObs(nx, ny);
                const g = this.board[ny]?.[nx];
                if (g && g.special !== SpecialType.NONE && g !== sp) this.chainQueue.push(g);
              }
            }
          } else if (sp.special === SpecialType.ROCKET) {
            for (let x = 0; x < this.cfg.width; x++) { toDestroy.add(`${x},${sp.gridY}`); if (this.obs[sp.gridY]?.[x]) this.reduceObs(x, sp.gridY);
              const g = this.board[sp.gridY]?.[x]; if (g && g.special !== SpecialType.NONE && g !== sp) this.chainQueue.push(g); }
            for (let y = 0; y < this.cfg.height; y++) { toDestroy.add(`${sp.gridX},${y}`); if (this.obs[y]?.[sp.gridX]) this.reduceObs(sp.gridX, y);
              const g = this.board[y]?.[sp.gridX]; if (g && g.special !== SpecialType.NONE && g !== sp) this.chainQueue.push(g); }
          } else if (sp.special === SpecialType.COLOR_BOMB) {
            const tt = this.board[match[0].y]?.[match[0].x]?.type ?? GemType.RED;
            for (let yy = 0; yy < this.cfg.height; yy++) for (let xx = 0; xx < this.cfg.width; xx++) {
              const g = this.board[yy]?.[xx]; if (g && g.type === tt) toDestroy.add(`${xx},${yy}`);
              if (g && g.type === tt && g.special !== SpecialType.NONE && g !== sp) this.chainQueue.push(g);
            }
          }
          await this.fxActivator(sp, match);
        }

        if (createSp) toDestroy.delete(`${createSp.x},${createSp.y}`);
        for (const ug of gemsToUpgrade) toDestroy.delete(`${ug.gridX},${ug.gridY}`);

        for (const key of toDestroy) {
          const [gx, gy] = key.split(',').map(Number);
          const g = this.board[gy]?.[gx];
          if (g) this.kill(g);
        }

        // Upgrade specials
        for (const ug of gemsToUpgrade) {
          ug.level = 2;
          const o = this.obs[ug.gridY]?.[ug.gridX];
          const px = ug.sprite ? ug.sprite.x : this.gp(ug.gridX, ug.gridY).x;
          const py = ug.sprite ? ug.sprite.y : this.gp(ug.gridX, ug.gridY).y;
          if (ug.sprite && ug.sprite.parent) ug.sprite.parent.removeChild(ug.sprite);
          if (ug.sprite) ug.sprite.destroy();
          ug.sprite = mkSprite(ug.type, ug.special, 2, this.cs, o?.type, o?.hits);
          ug.sprite.x = px; ug.sprite.y = py; ug.sprite.scale.set(0.5);
          this.gL.addChild(ug.sprite);
          await tw(ug.sprite.scale, { x: 1, y: 1 }, 300, eOB);
          this.parts.burst(px, py, 0xffaa00, 12, 3, 3);
          soundManager.colorBomb();
        }

        // Create new special
        if (createSp) {
          const o = this.obs[createSp.y]?.[createSp.x];
          const ng = this.makeGem(createSp.x, createSp.y, createSp.type, createSp.sp, createSp.lvl);
          this.board[createSp.y][createSp.x] = ng;
          ng.sprite.scale.set(0.15); ng.sprite.alpha = 0;
          this.gL.addChild(ng.sprite);
          await Promise.all([tw(ng.sprite.scale, { x: 1.15, y: 1.15 }, 250, eOB), tw(ng.sprite, { alpha: 1 }, 150, eOC)]);
          await tw(ng.sprite.scale, { x: 1, y: 1 }, 100, eOC);
          if (createSp.sp === SpecialType.BOMB) soundManager.bombExplode();
          else if (createSp.sp === SpecialType.COLOR_BOMB) soundManager.colorBomb();
          else soundManager.rocketLaunch();
        }

        this.onScore?.(this.score);
      }

      // Process any chain reactions from cascade
      await this.processChainQueue();

      // Drop and fill
      await this.drop();
      await this.fill();
      this.renderAll();
      current = this.findMatches();
    }
    this.renderAll();
  }

  // ─── Obstacles ─────────────────────────────────────────────────
  private reduceObs(gx: number, gy: number): void {
    const o = this.obs[gy]?.[gx];
    if (!o) return;
    o.hits--;
    if (o.hits <= 0) {
      this.obs[gy][gx] = null;
      const p = this.gp(gx, gy);
      this.parts.burst(p.x, p.y, 0xa8d8ea, 8);
      soundManager.bombExplode();
    } else {
      const g = this.board[gy]?.[gx];
      if (g) {
        if (g.sprite && g.sprite.parent) g.sprite.parent.removeChild(g.sprite);
        if (g.sprite) g.sprite.destroy();
        g.sprite = mkSprite(g.type, g.special, g.level, this.cs, o.type, o.hits);
        const p = this.gp(gx, gy); g.sprite.x = p.x; g.sprite.y = p.y;
        this.gL.addChild(g.sprite);
        const pp = this.gp(gx, gy);
        this.parts.burst(pp.x, pp.y, 0xa8d8ea, 4);
      }
    }
  }

  private async fxActivator(gem: Gem, match: { x: number; y: number }[]) {
    const px = gem.sprite ? gem.sprite.x : this.gp(gem.gridX, gem.gridY).x;
    const py = gem.sprite ? gem.sprite.y : this.gp(gem.gridX, gem.gridY).y;
    if (gem.special === SpecialType.BOMB) {
      soundManager.bombExplode();
      await Promise.all([this.flash(0xff4444, 0.4, 200), this.shake(6, 250), this.eRing(px, py, 0xff4444, this.cs * (gem.level >= 2 ? 5 : 4), 400)]);
      this.parts.burst(px, py, 0xff4444, 25, 5, 5);
    } else if (gem.special === SpecialType.ROCKET) {
      soundManager.rocketLaunch();
      await Promise.all([this.flash(0x00ff88, 0.3, 150), this.shake(3, 150), this.sweep(px, py, true, 0x00ff88, this.cfg.width * this.cs, 350), this.sweep(px, py, false, 0x00ccff, this.cfg.height * this.cs, 350)]);
      this.parts.burst(px, py, 0x00ff88, 20, 5, 5);
    } else if (gem.special === SpecialType.COLOR_BOMB) {
      soundManager.colorBomb();
      const tt = this.board[match[0].y]?.[match[0].x]?.type ?? GemType.RED;
      const color = GEM_COLORS[tt]?.[1] ?? 0xffaa00;
      await Promise.all([this.cWave(px, py, color, Math.max(this.app.screen.width, this.app.screen.height), 600), this.flash(color, 0.3, 250), this.shake(5, 300)]);
      this.parts.burst(px, py, color, 30, 5, 5);
    }
  }

  private kill(gem: Gem) {
    this.score += 10; this.gUpdate(gem);
    if (gem.sprite) {
      const color = GEM_COLORS[gem.type]?.[1] ?? 0xffffff;
      this.parts.burst(gem.sprite.x, gem.sprite.y, color, 8);
      if (gem.sprite.parent) gem.sprite.parent.removeChild(gem.sprite);
      gem.sprite.destroy();
    }
    gem.sprite = null;
    this.board[gem.gridY][gem.gridX] = null;
  }

  private async drop() {
    const { width: W, height: H } = this.cfg;
    const drops: Promise<void>[] = [];
    for (let x = 0; x < W; x++) {
      let empty = 0;
      for (let y = H - 1; y >= 0; y--) {
        if (!this.board[y][x]) empty++;
        else if (empty > 0) {
          const gem = this.board[y][x]!;
          const ny = y + empty;
          this.board[ny][x] = gem; this.board[y][x] = null;
          gem.gridY = ny;
          drops.push(tw(gem.sprite!, { y: ny * this.cs + this.cs / 2 }, 260, eOBo));
        }
      }
    }
    if (drops.length) { await Promise.all(drops); soundManager.drop(Math.min(drops.length, 6)); }
  }

  private async fill() {
    const { width: W, height: H } = this.cfg;
    const fills: Promise<void>[] = [];
    for (let x = 0; x < W; x++) {
      let empty = 0;
      for (let y = 0; y < H; y++) {
        if (!this.board[y][x]) {
          empty++;
          const gem = this.makeGem(x, y, (Math.floor(Math.random() * this.cfg.gemTypes) as GemType), SpecialType.NONE, 0);
          this.board[y][x] = gem;
          gem.sprite!.y = -empty * this.cs - 40; gem.sprite!.alpha = 0; gem.sprite!.scale.set(0.3);
          this.gL.addChild(gem.sprite!);
          const ty = y * this.cs + this.cs / 2;
          fills.push(Promise.all([tw(gem.sprite!, { y: ty }, 300, eOBo), tw(gem.sprite!, { alpha: 1 }, 120, eOC), tw(gem.sprite!.scale, { x: 1, y: 1 }, 300, eOB)]).then(() => {}));
        }
      }
    }
    if (fills.length) await Promise.all(fills);
  }

  // ─── Effects ─────────────────────────────────────────────────────
  private async shake(intensity = 4, dur = 200) {
    const t0 = performance.now();
    await new Promise<void>(resolve => {
      const tick = () => { const p = Math.min((performance.now() - t0) / dur, 1); this.bC.x += (Math.random() - 0.5) * intensity * (1 - p); this.bC.y += (Math.random() - 0.5) * intensity * (1 - p); p < 1 ? requestAnimationFrame(tick) : resolve(); };
      requestAnimationFrame(tick);
    });
    this.dim();
  }
  private async flash(color: number, alpha = 0.3, dur = 150) {
    const g = new PIXI.Graphics(); g.rect(-2000, -2000, 4000, 4000);
    g.fill({ color, alpha }); this.fxL.addChild(g);
    await tw(g, { alpha: 0 }, dur, eOC);
    this.fxL.removeChild(g); g.destroy();
  }
  private async eRing(x: number, y: number, color: number, maxR: number, dur = 500) {
    const ring = new PIXI.Graphics(); ring.circle(0, 0, 3); ring.stroke({ color, width: 5, alpha: 0.8 });
    ring.x = x; ring.y = y; this.fxL.addChild(ring);
    const t0 = performance.now();
    await new Promise<void>(resolve => {
      const tick = () => { const p = Math.min((performance.now() - t0) / dur, 1); ring.scale.set(1 + (maxR - 3) * eOC(p) / 3); ring.alpha = 1 - eIQ(p); p < 1 ? requestAnimationFrame(tick) : resolve(); };
      requestAnimationFrame(tick);
    });
    this.fxL.removeChild(ring); ring.destroy();
  }
  private async sweep(x: number, y: number, h: boolean, color: number, length: number, dur = 400) {
    const line = new PIXI.Graphics();
    if (h) line.rect(x - length / 2, y - 4, length, 8); else line.rect(x - 4, y - length / 2, 8, length);
    line.fill({ color, alpha: 0.8 }); this.fxL.addChild(line);
    await new Promise<void>(resolve => {
      const t0 = performance.now();
      const tick = () => { const p = Math.min((performance.now() - t0) / dur, 1); line.alpha = 1 - eIQ(p); p < 1 ? requestAnimationFrame(tick) : resolve(); };
      requestAnimationFrame(tick);
    });
    this.fxL.removeChild(line); line.destroy();
  }
  private async cWave(x: number, y: number, color: number, maxR: number, dur = 600) {
    const ps: Promise<void>[] = [];
    for (let i = 0; i < 3; i++) {
      ps.push((async () => {
        await new Promise(r => setTimeout(r, i * 100));
        const ring = new PIXI.Graphics(); ring.circle(0, 0, 3); ring.stroke({ color, width: 5, alpha: 0.7 });
        ring.x = x; ring.y = y; this.fxL.addChild(ring);
        const t0 = performance.now();
        await new Promise<void>(resolve => {
          const tick = () => { const p = Math.min((performance.now() - t0) / (dur - i * 60), 1); ring.scale.set(1 + (maxR - 3) * eOC(p) / 3); ring.alpha = 1 - eIQ(p); p < 1 ? requestAnimationFrame(tick) : resolve(); };
          requestAnimationFrame(tick);
        });
        this.fxL.removeChild(ring); ring.destroy();
      })());
    }
    await Promise.all(ps);
  }

  private async aSwap(a: Gem, b: Gem) {
    const ax = a.sprite!.x, ay = a.sprite!.y, bx = b.sprite!.x, by = b.sprite!.y;
    await Promise.all([tw(a.sprite!, { x: bx, y: by }, 120, eOC), tw(b.sprite!, { x: ax, y: ay }, 120, eOC)]);
  }
  private adj(a: Gem, b: Gem) { return Math.abs(a.gridX - b.gridX) + Math.abs(a.gridY - b.gridY) === 1; }
  private gSwap(a: Gem, b: Gem) {
    const tx = a.gridX, ty = a.gridY;
    a.gridX = b.gridX; a.gridY = b.gridY; b.gridX = tx; b.gridY = ty;
    this.board[a.gridY][a.gridX] = a; this.board[b.gridY][b.gridX] = b;
  }
  private gUpdate(gem: Gem) {
    for (const g of this.goals) {
      if (g.type === 'score') g.currentCount = this.score;
      else if (g.type === 'destroy' && (!g.targetType || g.targetType === GemType[gem.type].toLowerCase())) g.currentCount++;
    }
    this.onGoals?.(this.goals);
  }
  private checkEnd() {
    const ok = this.goals.length ? this.goals.every(g => g.currentCount >= g.targetCount) : false;
    this.onEnd?.(ok, this.score);
  }

  resize() { this.dim(); this.drawBg(); this.renderAll(); }
  pause() {} resume() {}
  getGemCount() { let c = 0; for (const r of this.board) for (const g of r) if (g) c++; return c; }
  getScore() { return this.score; }
  getMoves() { return this.movesLeft; }
  getGoals() { return this.goals; }
}
